"use client";

import React from "react";
import { twMerge } from "tailwind-merge";
import { BrandedNavHeader, type BrandedNavHeaderProps } from "./nav-header";

interface ListPageLayoutProps {
  children: React.ReactNode;
  navHeaderProps: BrandedNavHeaderProps;
  /** Override the outermost container classes (bg, height). */
  layoutClassName?: string;
  /** Override the scrollable content area classes (padding, max-width). Replaces the default kinsa container styling. */
  contentClassName?: string;
  /** Inline styles for the scrollable content area, for values a literal Tailwind class can't express. */
  contentStyle?: React.CSSProperties;
}

/**
 * Shared layout component for pages with consistent header and container structure.
 */
export function ListPageLayout({
  children,
  navHeaderProps,
  layoutClassName,
  contentClassName,
  contentStyle,
}: ListPageLayoutProps) {
  // WARN: If in browser or in example app modal, --felt-safe-area will not be set by SDK and layout will look wrong! Ignore this
  const defaultNavClassName = navHeaderProps.theme
    ? undefined
    : "bg-background/60 backdrop-blur-md";

  return (
    <div
      className={twMerge(
        "h-[calc(100dvh-var(--felt-safe-area-top,0px))] overflow-y-auto bg-background",
        layoutClassName
      )}
    >
      {/* Fixed header */}
      <BrandedNavHeader
        showLogo
        {...navHeaderProps}
        className={twMerge(defaultNavClassName, navHeaderProps.className)}
      />

      {/* Main content */}
      {contentClassName ? (
        <div className={contentClassName} style={contentStyle}>
          {children}
        </div>
      ) : (
        <div className="pt-[64px] pb-[72px]">
          <main className="container mx-auto px-4 max-w-2xl">{children}</main>
        </div>
      )}
    </div>
  );
}
